import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCacheEfficiency;

export type CacheEfficiencyQueryData = {
  range: TimeRange;
  stepSeconds: number;
  items: Required<ApiResponse<typeof apiFn>>['items'];
};

type CacheEfficiencyQueryArgs<SELECT> = QueryOptions<
  CacheEfficiencyQueryData,
  SELECT
> & {
  cacheGroupId?: string;
  cacheId?: string;
  username?: string;
  templateHash?: string;
  key: string;
  stepSeconds?: number;
  getRange: () => TimeRange;
};

const DBO_CACHE_EFFICIENCY_QUERY_KEY = 'dbo-cache-efficiency';

const getCacheEfficiencyQueryKey = (
  params: {
    key?: string;
    cacheGroupId?: string;
    cacheId?: string;
    templateHash?: string;
    username?: string;
    stepSeconds?: number;
  } = {}
) =>
  [
    DBO_CACHE_EFFICIENCY_QUERY_KEY,
    params.key,
    params.cacheGroupId,
    params.cacheId,
    params.templateHash,
    params.username,
    params.stepSeconds,
  ] as const;

const getCacheEfficiencyQueryOptions = ({
  cacheGroupId,
  cacheId,
  templateHash,
  username,
  key,
  stepSeconds = 3600,
  getRange,
  enabled,
}: Omit<CacheEfficiencyQueryArgs<unknown>, 'select'>) =>
  queryOptions({
    queryKey: getCacheEfficiencyQueryKey({
      key,
      cacheGroupId,
      cacheId,
      username,
      templateHash,
      stepSeconds,
    }),
    queryFn: async () => {
      const range = getRange();
      const { data } = await apiFn({
        groupId: cacheGroupId!,
        cacheId: cacheId!,
        username: username || undefined,
        templateHash: templateHash || undefined,
        startTime: range.from,
        endTime: range.to,
        stepSeconds,
      });
      return {
        items: data.items || [],
        range,
        stepSeconds,
      };
    },
    enabled: makeIsEnabled(enabled, !!cacheGroupId, !!cacheId),
  });

export const useCacheEfficiencyQuery = <SELECT = CacheEfficiencyQueryData>({
  cacheGroupId,
  cacheId,
  templateHash,
  username,
  key,
  stepSeconds = 3600,
  getRange,
  enabled,
  select,
  refetchInterval,
}: CacheEfficiencyQueryArgs<SELECT>) => {
  return useQuery({
    ...getCacheEfficiencyQueryOptions({
      cacheGroupId,
      cacheId,
      templateHash,
      username,
      key,
      stepSeconds,
      getRange,
      enabled,
    }),
    select,
    refetchInterval,
  });
};
